import React, { FC, useEffect, ReactNode, useState } from "react";
import classNames from "classnames";
import { useSelect, UseSelectProps } from "downshift";

import { FieldStatus } from "../../Helpers/FieldStatus";

import { SpecializationDto } from "../../../../../Api/Model/SpecializationDto.csharp";
import { SelectItemContent } from "./SelectItemContent";
import { interpolate as inter } from "../../Helpers";
import { DoctorDto } from "../../../../../Api/Model/DoctorDto.csharp";
import { FirstStepBookingLabelsDto } from "../../../../../Api/Model/BookingSteps/FirstStepBookingLabelsDto.csharp";
import { LocationDto } from "../../../../../Api/Model/LocationDto.csharp";
import { FieldErrorPosition } from "../../Helpers/interfaces";

type SpecializationSelectProps = {
  specializations: UseSelectProps<SpecializationDto | null>;
  name: string;
  label: string;
  placeholder: string;
  onSelect: (specialization: SpecializationDto, userInput: boolean, clearServicesInput: boolean) => void;
  unavailableSpecializations: {
    items: SpecializationDto[];
  };
  status?: FieldStatus;
  error?: {
    message?: string;
  };
  defaultValue?: SpecializationDto;
  boldNameOnly?: boolean;
  location: LocationDto;
  doctor: DoctorDto;
  additionalLabels: FirstStepBookingLabelsDto;
  errorPosition?: FieldErrorPosition;
};

export const SpecializationSelect: FC<SpecializationSelectProps> = ({
  specializations,
  name,
  label,
  placeholder,
  onSelect,
  unavailableSpecializations = {
    items: [],
  },
  status = FieldStatus.active,
  error,
  defaultValue = undefined,
  boldNameOnly = false,
  location,
  doctor,
  additionalLabels,
  errorPosition = FieldErrorPosition.left,
}) => {
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    selectItem,
  } = useSelect(specializations);

  const [shouldClearServicesAfterChange, setShouldClearServicesAfterChange] = useState<boolean>(false);

  const id = `input-${name}`;

  useEffect(() => {
    if (doctor.id && doctor.id != "-1" && specializations.items.length == 1) {
      setShouldClearServicesAfterChange(false);
      selectItem(specializations.items[0]);
      onSelect(specializations.items[0]!, true, false);
    }
  }, [specializations.items.length, doctor.id]);

  useEffect(() => {
    if (selectedItem != null && selectedItem.id) {
      onSelect(selectedItem, true, shouldClearServicesAfterChange);
      setShouldClearServicesAfterChange(true);
    }
  }, [selectedItem]);

  function getUnavailableSpecializationsSubheading() {
    if (doctor.id && doctor.id != "-1")
      return inter(additionalLabels.specNotByDoctor, "$doctor", doctor.firstName);
    if (location.id && location.id != "-1") {
      return location.isLocation
        ? inter(additionalLabels.specNotByLocation, "$location", location.name)
        : inter(additionalLabels.specNotByCenter, "$center", location.name);
    }
    return "";
  }

  const mapOptionsElements = (
    item: SpecializationDto | null,
    index: number
  ): ReactNode => {
    return (
      <li
        key={item!.id}
        className={classNames("select__option p-1", {
          "select__option--highlight": highlightedIndex === index,
        })}
        {...getItemProps({ item, index })}
      >
        <SelectItemContent boldNameOnly={boldNameOnly} {...item} />
      </li>
    );
  };

  const mapRestOptionsElements = (item: SpecializationDto) => {
    return (
      <li key={item!.id} className={"select__option p-1"}>
        <SelectItemContent boldNameOnly={boldNameOnly} {...item} />
      </li>
    );
  };

  const madeSelection =
    selectedItem != null || (defaultValue && defaultValue.id);

  return (
    <div
      className={classNames(
        "position-relative my-2 select booking-narrow-item",
        {
          "select--disabled": status == FieldStatus.disabled,
          "select--error": status == FieldStatus.error,
        }
      )}
    >
      <button
        className="ps-1 py-1 pe-3 select__field"
        type="button"
        {...getToggleButtonProps()}
        disabled={status == FieldStatus.disabled}
      >
        {selectedItem?.name ?? defaultValue?.name ?? ""}
      </button>
      <span
        {...getLabelProps()}
        className={classNames("position-absolute select__label", {
          "select__label--focus": madeSelection,
        })}
      >
        {!madeSelection && placeholder}
        {madeSelection && label}
      </span>
      <ul className="select__optionsList" {...getMenuProps()}>
        {isOpen && (
          <>
            {specializations.items.map(mapOptionsElements)}
            {unavailableSpecializations.items.length > 0 && (
              <>
                <li className="select__option p-1 pt-2 select__optionGroupHeader unselectable-options-below border-top border-4 border-info">
                  {getUnavailableSpecializationsSubheading()}
                </li>
                {unavailableSpecializations.items.map(mapRestOptionsElements)}
              </>
            )}
          </>
        )}
      </ul>
      {status == FieldStatus.error && error && error.message && (
        <div
          className={classNames("input__support p-1", "input__support--error", {
            "input__support--errorLeft":
              errorPosition == FieldErrorPosition.left,
            "input__support--errorBottom":
              errorPosition == FieldErrorPosition.bottom,
          })}
        >
          <p id={"errorId-" + id} className="fs-6 input__supportText">
            {error.message}
          </p>
        </div>
      )}
    </div>
  );
};
